import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import { ALIGN_BUDGET_UNIT } from '@atom/graph/budget';
import { Snackbar } from '@atom/mui';
import {
  BudgetAlignErrorType,
  BudgetCategory,
  BudgetUnitAlignInput,
  BudgetUnitAlignPreview,
} from '@atom/types/budget';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import BudgetDetailContext from '../BudgetDetailContext';

import { BudgetAlignView } from './BudgetAlignModal';

interface Props {
  onClose?: () => void;
}

const findZeroValueIds = (category: BudgetCategory): string[] =>
  (category.budgetItems ?? [])
    .filter(item => item?.budgetValue === 0)
    .map(item => item.id);

const getBudgetItemZeroIds = (budgetCategories: BudgetCategory[]) =>
  new Set(R.chain(findZeroValueIds, budgetCategories));

interface UseBudgetAlignReturn {
  view: BudgetAlignView;
  setView: Dispatch<SetStateAction<BudgetAlignView>>;
  handleSubmit: () => void;
  submitAlignBudgetUnit: useMutation.MutationFunction<
    { budgetUnitAlign: BudgetUnitAlignPreview },
    { input: BudgetUnitAlignInput }
  >;
  getBudgetUnitAlignPreview: useMutation.MutationFunction<
    { budgetUnitAlign: BudgetUnitAlignPreview },
    { input: BudgetUnitAlignInput }
  >;
  previewData: BudgetUnitAlignPreview;
  errorData: Set<BudgetAlignErrorType>;
  selectedItems: Set<string>;
}

const useBudgetAlignModal = ({ onClose }: Props): UseBudgetAlignReturn => {
  const {
    budget,
    budgetCategories,
    fetchParentUnit,
    parentBudgetUnit,
    selectedItems,
  } = useContext(BudgetDetailContext);
  const [view, setView] = useState(BudgetAlignView.LOADING);
  const [errorData, setErrorData] = useState<Set<BudgetAlignErrorType>>(
    new Set([]),
  );

  const zeroBudgetsSelected = useMemo(() => {
    const zeroIds = getBudgetItemZeroIds(budgetCategories);
    return selectedItems.intersection(new Set(zeroIds));
  }, [budgetCategories, selectedItems]);

  const getAlignInput = (validateOnly: boolean): BudgetUnitAlignInput => ({
    budgetId: budget.id,
    budgetUnitId: parentBudgetUnit.id,
    // filter out selected budgets with zero value
    budgetItemIds: [...selectedItems.difference(zeroBudgetsSelected)],
    validateOnly,
  });

  const [getBudgetUnitAlignPreview, { data }] = useMutation(ALIGN_BUDGET_UNIT, {
    onCompleted: () => {
      setView(BudgetAlignView.MAIN);
    },
    onError: error => {
      Snackbar.error({
        message: error?.message || 'There was a network error',
        action: 'Try Again',
        onDismiss: onClose,
        onActionClick: () =>
          getBudgetUnitAlignPreview({
            variables: {
              input: getAlignInput(true),
            },
          }),
      });
    },
  });

  const [submitAlignBudgetUnit] = useMutation(ALIGN_BUDGET_UNIT, {
    onCompleted: () => {
      fetchParentUnit({ budgetUnitId: parentBudgetUnit.id });
      onClose();
    },
    onError: error => {
      Snackbar.error({
        message: error?.message || 'There was a network error',
      });
    },
  });

  useEffect(() => {
    if (!parentBudgetUnit?.authorizedBudget) {
      setView(BudgetAlignView.ERROR);
      setErrorData(
        errorData.add(BudgetAlignErrorType.UNIT_HAS_NO_AUTHORIZED_VALUE),
      );
      return;
    }

    if (zeroBudgetsSelected.size === selectedItems.size) {
      setView(BudgetAlignView.ERROR);
      setErrorData(
        errorData.add(BudgetAlignErrorType.SELECTED_ITEMS_WITH_ZERO_VALUE),
      );
      return;
    }

    getBudgetUnitAlignPreview({
      variables: {
        input: getAlignInput(true),
      },
    });
  }, []);

  const handleSubmit = () => {
    submitAlignBudgetUnit({
      variables: {
        input: getAlignInput(false),
      },
    });
  };

  const previewData: BudgetUnitAlignPreview = R.pathOr(
    null,
    ['budgetUnitAlign'],
    data,
  );

  useEffect(() => {
    if (!isNilOrEmpty(previewData)) {
      if (previewData?.availableForSelected < 0) {
        setView(BudgetAlignView.ERROR);
        setErrorData(
          errorData.add(BudgetAlignErrorType.NONE_AVAILABLE_FOR_SELECTED),
        );
      }
      if (previewData?.adjustedItemCount <= 0) {
        setView(BudgetAlignView.ERROR);
        setErrorData(
          errorData.add(BudgetAlignErrorType.NO_ADJUSTED_ITEM_COUNT),
        );
      }
      if (previewData?.adjustmentNeeded === 0) {
        setView(BudgetAlignView.ERROR);
        setErrorData(errorData.add(BudgetAlignErrorType.NO_ADJUSTMENT_NEEDED));
      }
    }
  }, [previewData]);

  return {
    view,
    setView,
    handleSubmit,
    submitAlignBudgetUnit,
    getBudgetUnitAlignPreview,
    previewData,
    errorData,
    selectedItems,
  };
};

export default useBudgetAlignModal;
